import {
  AppliedDiscountFragment,
  ComWixEcomTotalsCalculatorV1AppliedDiscountDiscountType,
} from '../../../../gql/graphql';
import {
  Discount,
  DiscountSourceType,
  DiscountScope as DiscountScopeV2,
  DiscountScopeWithLiterals,
  DiscountSourceTypeWithLiterals,
} from '@wix/auto_sdk_ecom_cart-v-2';
import {ModelContext} from '../../../../types/checkoutApp.types';
import {SPECS} from '../../../../common/constants';

export class DiscountModel {
  public name: string;
  public scope: DiscountScope;
  public sourceType: DiscountSource;
  public totalAmountOff: string;
  public targetLineItemIds: string[];

  constructor(params: DiscountModel) {
    this.name = params.name;
    this.scope = params.scope;
    this.totalAmountOff = params.totalAmountOff;
    this.targetLineItemIds = params.targetLineItemIds;
    this.sourceType = params.sourceType;
  }

  public static fromCheckoutV1(discount: AppliedDiscountFragment | null) {
    if (!discount?.coupon) {
      return undefined;
    }

    const scope =
      discount.discountType === ComWixEcomTotalsCalculatorV1AppliedDiscountDiscountType.SHIPPING
        ? DiscountScope.DELIVERY
        : DiscountScope.CART_SUBTOTAL;

    return new DiscountModel({
      name: discount.coupon.code ?? /* istanbul ignore next */ '',
      scope,
      totalAmountOff: '',
      targetLineItemIds: [],
      sourceType: DiscountSource.COUPON,
    });
  }

  public static fromCartV2(discount: Discount, modelContext: ModelContext) {
    return new DiscountModel({
      name:
        discount.source?.couponCode ??
        discount?.name?.translated ??
        discount?.name?.original ??
        /* istanbul ignore next */ '',
      scope: toCouponDiscountScope(discount?.scope),
      totalAmountOff: modelContext.formatAmount(discount.total?.convertedAmount ?? /* istanbul ignore next */ ''),
      targetLineItemIds: modelContext.flowAPI.experiments.enabled(SPECS.UseLineItemDiscountInCartSummary)
        ? discount?.lineItemDiscounts?.map((lid) => lid.lineItemId).filter((id): id is string => id !== undefined) ??
          /* istanbul ignore next */ []
        : discount?.benefits?.map((benefit) => benefit.lineItemId).filter((id): id is string => id !== undefined) ??
          /* istanbul ignore next */ [],
      sourceType: toDiscountSource(discount.source!.sourceType!),
    });
  }
}

export enum DiscountScope {
  CART_SUBTOTAL = 'CART_SUBTOTAL',
  LINE_ITEM = 'LINE_ITEM',
  DELIVERY = 'DELIVERY',
}

export enum DiscountSource {
  COUPON = 'COUPON',
  AUTOMATIC_RULE = 'AUTOMATIC_RULE',
}

const toDiscountSource = (discountSource: DiscountSourceTypeWithLiterals): DiscountSource =>
  DISCOUNT_SOURCE_MAP[discountSource];

const toCouponDiscountScope = (discountScope?: DiscountScopeWithLiterals) =>
  (discountScope && DISCOUNT_SCOPE_MAP[discountScope]) ?? DiscountScope.CART_SUBTOTAL;

const DISCOUNT_SCOPE_MAP: Record<DiscountScopeWithLiterals, DiscountScope> = {
  [DiscountScopeV2.DELIVERY]: DiscountScope.DELIVERY,
  [DiscountScopeV2.LINE_ITEM]: DiscountScope.LINE_ITEM,
  [DiscountScopeV2.CART_SUBTOTAL]: DiscountScope.CART_SUBTOTAL,
  [DiscountScopeV2.UNKNOWN_DISCOUNT_SCOPE]: DiscountScope.CART_SUBTOTAL,
};

const DISCOUNT_SOURCE_MAP: Record<DiscountSourceTypeWithLiterals, DiscountSource> = {
  [DiscountSourceType.COUPON]: DiscountSource.COUPON,
  [DiscountSourceType.AUTOMATIC_RULE]: DiscountSource.AUTOMATIC_RULE,
};
